import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Shield, Award, Stethoscope, Brain } from 'lucide-react';
import Footer from './Footer';
import IsometricTechStack from './IsometricTechStack';
import { APP_NAME } from '../config/branding';
import { usePageTitle } from '../hooks/usePageTitle';
import { useMetaDescription } from '../hooks/useMetaDescription';

// Import founder images from public folder
const RobynneImage = '/robynne.jpg';

const AboutUs = () => {
  usePageTitle(`About ${APP_NAME} - How AI Phone Agents Transform Veterinary Practices`);
  useMetaDescription(`Learn how ${APP_NAME} is transforming veterinary practice management with AI-powered phone agents, visit transcriptions, and online booking. Founded by veterinary technology experts.`);

  return (
    <div className="min-h-screen bg-white dark:bg-dark-bg-primary">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-dark-bg-secondary dark:to-dark-bg-tertiary py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-6xl font-bold text-gray-900 dark:text-dark-text-primary mb-6" style={{
            fontFamily: 'Calibre, ui-sans-serif, system-ui, -apple-system, "system-ui", "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif'
          }}>
            About {APP_NAME}
          </h1>
          <p className="text-xl text-gray-600 dark:text-dark-text-secondary max-w-3xl mx-auto leading-relaxed">
            We're transforming veterinary practice management by streamlining visit documentation with iPhone recording and AI-powered transcription.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white dark:bg-dark-bg-primary">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-dark-text-primary mb-6" style={{
                fontFamily: 'Calibre, ui-sans-serif, system-ui, -apple-system, "system-ui", "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif'
              }}>
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 dark:text-dark-text-secondary mb-6 leading-relaxed">
                Our AI voice agent takes on up to 80% of call volume, handling both inbound and outbound conversations with intelligence and empathy. From scheduling appointments to answering routine questions, it keeps your office running smoothly while freeing your staff to focus on what matters most: patients and their owners.
              </p>
              <p className="text-lg text-gray-600 dark:text-dark-text-secondary mb-6 leading-relaxed">
                And because veterinary care is never one-size-fits-all, there's always a built-in emergency hatch. If a call requires a human touch, it seamlessly transfers to your team, ensuring safety, trust, and the best possible experience for pet owners.
              </p>
              <p className="text-lg text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                The result: a smarter, calmer, and more efficient practice where your staff spend less time on the phone and more time providing care.
              </p>
            </div>
            <div className="flex justify-center">
              <div className="w-80 h-80 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-dark-bg-secondary dark:to-dark-bg-tertiary rounded-3xl flex items-center justify-center p-8">
                <img
                  src="/logo_clear_back.png"
                  alt={APP_NAME}
                  className="w-64 h-64 object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Design Philosophy Section */}
      <section className="py-24 bg-white dark:bg-dark-bg-primary border-t border-gray-100 dark:border-dark-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
            {/* Left Column - Purple/Orange Pattern */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-dark-text-primary mb-10" style={{
                fontFamily: 'Calibre, ui-sans-serif, system-ui, -apple-system, "system-ui", "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif'
              }}>
                We've designed {APP_NAME} to:
              </h2>

              <div className="flex flex-col items-start space-y-6 w-full">
                <div className="bg-purple-50 dark:bg-purple-900/30 text-purple-900 dark:text-purple-200 px-6 py-4 rounded-xl text-xl inline-block">
                  Be <span className="font-bold">easy</span> to implement, <span className="font-bold">powerful</span> in practice
                </div>

                <div className="bg-orange-50 dark:bg-orange-900/30 text-orange-900 dark:text-orange-200 px-6 py-4 rounded-xl text-xl inline-block">
                  <span className="font-bold">Record and transcribe</span> visits into perfect SOAP notes
                </div>

                <div className="bg-purple-50 dark:bg-purple-900/30 text-purple-900 dark:text-purple-200 px-6 py-4 rounded-xl text-xl inline-block">
                  <span className="font-bold">Automate</span> front desk tasks with a human touch
                </div>

                <div className="bg-orange-50 dark:bg-orange-900/30 text-orange-900 dark:text-orange-200 px-6 py-4 rounded-xl text-xl inline-block">
                  Provide instant <span className="font-bold">visibility</span> into practice performance
                </div>

                <div className="bg-purple-50 dark:bg-purple-900/30 text-purple-900 dark:text-purple-200 px-6 py-4 rounded-xl text-xl inline-block">
                  Send <span className="font-bold">smart reminders</span> to increase patient retention
                </div>
              </div>
            </div>

            {/* Right Column - Alternating Sizes, Right Aligned */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-dark-text-primary mb-10" style={{
                fontFamily: 'Calibre, ui-sans-serif, system-ui, -apple-system, "system-ui", "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif'
              }}>
                To help you achieve:
              </h2>

              <div className="flex flex-col items-end space-y-8 w-full">
                {/* Large */}
                <div className="bg-pink-50 dark:bg-pink-900/30 text-pink-900 dark:text-pink-200 px-8 py-4 rounded-2xl text-2xl font-bold text-center">
                  Improved client experience
                </div>

                {/* Small/Wide */}
                <div className="bg-pink-50 dark:bg-pink-900/30 text-pink-900 dark:text-pink-200 px-6 py-2.5 rounded-xl text-lg font-medium text-center">
                  More accurate and detailed medical records
                </div>

                {/* Large */}
                <div className="bg-pink-50 dark:bg-pink-900/30 text-pink-900 dark:text-pink-200 px-10 py-5 rounded-2xl text-3xl font-bold text-center shadow-sm">
                  Veterinary team productivity
                </div>

                {/* Small */}
                <div className="bg-pink-50 dark:bg-pink-900/30 text-pink-900 dark:text-pink-200 px-5 py-2 rounded-lg text-base font-medium text-center">
                  Reduced administrative burden
                </div>

                {/* Large */}
                <div className="bg-pink-50 dark:bg-pink-900/30 text-pink-900 dark:text-pink-200 px-8 py-4 rounded-2xl text-2xl font-bold text-center">
                  Stronger, more consistent patient care
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="py-16 bg-gray-50 dark:bg-dark-bg-secondary">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-dark-text-primary mb-4" style={{
              fontFamily: 'Calibre, ui-sans-serif, system-ui, -apple-system, "system-ui", "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif'
            }}>
              Our Founders
            </h2>
            <p className="text-xl text-gray-600 dark:text-dark-text-secondary">Meet the team behind {APP_NAME}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {(() => {
              const teamMembers = [
                {
                  name: 'Robynne Merguerdijian',
                  role: 'Customer Success',
                  bio: 'Information architect who curates and structures veterinary knowledge for our Expert AI system. Transforms complex medical research, clinical studies, and treatment protocols into accessible data that powers evidence-based pet care recommendations.',
                  avatar: 'R',
                  image: RobynneImage,
                },
              ];

              return teamMembers.map((member, index) => (
                <div key={index} className="bg-white dark:bg-dark-bg-tertiary rounded-2xl p-8 shadow-sm text-center">
                  <div className="mb-6">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-32 h-32 rounded-full mx-auto object-cover mb-4"
                      onError={(e) => {
                        // Fallback to avatar if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div className="w-32 h-32 bg-blue-600 rounded-full mx-auto flex items-center justify-center text-white text-4xl font-bold hidden">
                      {member.avatar}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-2">{member.name}</h3>
                  <p className="text-lg text-blue-600 dark:text-blue-400 font-semibold mb-4">{member.role}</p>
                  <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">{member.bio}</p>
                </div>
              ));
            })()}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{
              fontFamily: 'Calibre, ui-sans-serif, system-ui, -apple-system, "system-ui", "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif'
            }}>
              Our Expertise
            </h2>
            <p className="text-xl text-gray-600">Combining technology with veterinary excellence</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Stethoscope className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">iPhone Recording Technology</h3>
                    <p className="text-gray-600">
                      Seamless audio recording directly from iPhone to secure cloud storage with presigned URLs and automatic transcription processing.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">AI Insights - Qualitative & Quantitative</h3>
                    <p className="text-gray-600">
                      Beyond transcription, our AI analyzes patterns, behavioral changes, and health indicators to provide actionable insights from visit conversations.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Multi-Practice Record Sharing</h3>
                    <p className="text-gray-600">
                      Less time on calls. Less time on paperwork. A secure, voice-first AI agent built for your clinic.                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="w-96 h-96 flex items-center justify-center">
                <IsometricTechStack />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to streamline your front desk?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join veterinary practices using {APP_NAME} to transform their front desk workflow.
          </p>
          <Link to="/contact" className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-colors">
            Start simplifying your front desk
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;